/**
 * Settings utilities for developer toolbar
 * Handles localStorage persistence for toolbar configuration
 */
import logger from 'js/app/loggerSingleton';

import localStorageEx from 'bundles/common/utils/localStorageEx';

const STORAGE_PREFIX = 'courseraToolbar';

export interface ToolbarSettings {
  isMinimized: boolean;
  position: {
    x: number | null;
    y: number | null;
  };
  bottomLocked: boolean;
  /** Plugin id -> enabled. Omitted plugin ids use plugin.defaultEnabled ?? true */
  pluginEnabled: Record<string, boolean>;
  /** Hide #cbk-epic, #cbk-api, #cbk-app extension banners to avoid duplication with toolbar (WWG-131) */
  hideExtensionBanners: boolean;
  /** Ordered plugin ids for toolbar; empty = use registry default order */
  pluginOrder: string[];
}

// Default settings
const defaultSettings: ToolbarSettings = {
  isMinimized: true,
  position: {
    x: null,
    y: null,
  },
  bottomLocked: false,
  pluginEnabled: {},
  hideExtensionBanners: true,
  pluginOrder: [],
};

/** Default settings (e.g. for SSR when localStorage is unavailable) */
export function getDefaultSettings(): ToolbarSettings {
  return { ...defaultSettings };
}

/**
 * Load all settings from localStorage
 */
export function loadSettings(): ToolbarSettings {
  try {
    const storedSettings = localStorageEx.getItem(`${STORAGE_PREFIX}.settings`, JSON.parse, null);
    if (storedSettings) {
      return {
        ...defaultSettings,
        ...storedSettings,
        pluginEnabled: { ...defaultSettings.pluginEnabled, ...storedSettings?.pluginEnabled },
        hideExtensionBanners:
          storedSettings?.hideExtensionBanners !== undefined
            ? storedSettings.hideExtensionBanners
            : defaultSettings.hideExtensionBanners,
        pluginOrder: Array.isArray(storedSettings?.pluginOrder)
          ? storedSettings.pluginOrder
          : defaultSettings.pluginOrder,
      };
    }
  } catch (error) {
    logger.error('Error loading coursera toolbar settings:', error);
  }
  return { ...defaultSettings };
}

/**
 * Save all settings to localStorage
 */
export function saveSettings(settings: ToolbarSettings): void {
  try {
    localStorageEx.setItem(`${STORAGE_PREFIX}.settings`, settings, JSON.stringify);
  } catch (error) {
    logger.error('Error saving coursera toolbar settings:', error);
  }
}

/**
 * Update a specific setting
 */
export function updateSetting<K extends keyof ToolbarSettings>(key: K, value: ToolbarSettings[K]): void {
  const currentSettings = loadSettings();
  const updatedSettings = {
    ...currentSettings,
    [key]: value,
  };
  saveSettings(updatedSettings);
}

/**
 * Set enabled state for a single plugin and persist
 */
export function setPluginEnabled(pluginId: string, enabled: boolean): void {
  const current = loadSettings();
  const next = {
    ...current,
    pluginEnabled: { ...current.pluginEnabled, [pluginId]: enabled },
  };
  saveSettings(next);
}

/**
 * Get a specific setting value
 */
export function getSetting<K extends keyof ToolbarSettings>(key: K): ToolbarSettings[K] {
  const settings = loadSettings();
  return settings[key];
}
